import { Injectable } from '@angular/core';
import { Observable, Subject, BehaviorSubject, lastValueFrom } from 'rxjs';
import { UpgradeInfo, SiteFile } from './upgrade.info.interface';
import { UpgradeProcess } from './upgrade.process';
import { AuthHttp } from '@http';
import { UrlEnhancerService } from '@url';
import { Config } from '@config';
import { RoutingInfoService } from '@routing';
import {  custom } from 'devextreme/ui/dialog';
import { map } from 'rxjs/operators';
import { TenantService } from '@tenant';
import { __i18n } from '@localisation';
import { SerializedPollService } from 'modules/tools-module/serialized-poll.service';


export enum UpgradeCommands {
    restartcontroller = 'restartcontroller',
    stopcontrollerapplication = 'stopcontrollerapplication',
    startcontrollerapplication = 'startcontrollerapplication',
    startupgrade = 'startupgrade',
    uploadcloudmapping = 'uploadcloudmapping',
    scheduleautomaticupgrade = 'scheduleautomaticupgrade',
    uploadbacnetfile = 'uploadbacnetfile',
    createanduploadparameterfile = 'createanduploadparameterfile',
    createanduploadcontrollertracefile = 'createanduploadcontrollertracefile',
    sendfiletocontroller = 'sendfiletocontroller',
    sendparameterfiletocontroller = 'sendparameterfiletocontroller'
}

@Injectable()
export class UpgradeService {

    public upgradeInfoStream: Subject<UpgradeInfo> = new Subject();

    public upgradeInfo: Observable<UpgradeInfo> = this.upgradeInfoStream.asObservable();

    public siteFilesStream: Subject<SiteFile[]> = new BehaviorSubject([]);

    public siteFiles: Observable<SiteFile[]> = this.siteFilesStream.asObservable();

    constructor(
        protected authHttp: AuthHttp,
        protected urlEnhancerService: UrlEnhancerService,
        protected config: Config,
        private routingInfoService: RoutingInfoService,
        private tenantService: TenantService
    ) {}

    private async wait(time){
        return new Promise((resolve, reject) => {
            setTimeout(resolve, time);
        });
    }

    public async isPlantOnline(){
        const plantId = this.routingInfoService.getCurrentRoutingParams().plantid;
        const data = new FormData();
        data.append('ids[0]', plantId);
        const status: any = await lastValueFrom(this.authHttp
            .post(this.config.services.status.plantOnlineStatusUrl + this.tenantService.getCurrentTenantId(), data)
            .pipe(map((res) => res.json()))
        );

        return (status[plantId] === true) ? true : false;
    }

    public clearSendFileCache(){
        this.sendFileActions = {};
    }

    private sendFileActions: any = {};
    public getSendFileAction(siteFile: any){
        const actionName = siteFile.deviceFriendlyName + 'send';

        if(!this.sendFileActions[actionName]){
            this.sendFileActions[actionName] = new UpgradeProcess(actionName, async() => {
                if(await this.openConfirmDialog(
                    __i18n('Confirm', 'upgrade.confirm.action.dialog.topic'),
                    __i18n('Really send to plant controller?', 'upgrade.confirm.action.send-to-controller.text')
                )) {

                    const destination  = siteFile.fileName
                    const source = siteFile.associatedFile.id;
                    const url = this.urlEnhancerService.replaceParams(this.config.services.upgrade.sendFile, {
                        commandname : UpgradeCommands.sendparameterfiletocontroller,
                        filename : source,
                        destinationfilename : destination,

                    });
                    const result = await lastValueFrom(this.authHttp.post(url, {}));
                    return result.json();

                }
                return false;
            }, 20);
        }
        return this.sendFileActions[actionName];
    }

    public getDownloadFileAction(siteFile: any){
        const actionName = siteFile.deviceFriendlyName + 'download';

        if(!this.sendFileActions[actionName]){
            this.sendFileActions[actionName] = new UpgradeProcess(actionName, async() => {
                if(await this.openConfirmDialog(
                    __i18n('Confirm', 'upgrade.confirm.action.dialog.topic'),
                    __i18n('Really upload from plant controller?', 'upgrade.confirm.action.download-from-controller.text')
                )) {

                    const destination  = siteFile.fileName
                    const url = this.urlEnhancerService.replaceParams(this.config.services.upgrade.downloadFileFromController, {
                        destinationfilename : destination

                    });
                    const result = await lastValueFrom(this.authHttp.post(url, {}));
                    return result.json();

                }
                return false;
            }, 20);
        }
        return this.sendFileActions[actionName];
    }

    public parameterFileAction(parameterBox: any, actionName: string){
        if(!this.sendFileActions[actionName]){
            this.sendFileActions[actionName] = new UpgradeProcess(actionName, async() => {
                if(await this.openConfirmDialog(
                    __i18n('Confirm', 'upgrade.confirm.action.dialog.topic'),
                    __i18n('Really send to plant controller?', 'upgrade.confirm.action.dialog.send-to-controller.text')
                )) {
                    const siteFile = parameterBox.getSelectedEntry();
                    const fileName = (actionName === 'parameter') ? 'R:CSLPARA.BIN' : 'F:BACNET.CSV' ;
                    let url = this.getParameterFileUrlActionName(actionName);
                    url = this.urlEnhancerService.replaceParams(url, {
                        commandname : UpgradeCommands.sendparameterfiletocontroller,
                        filename : this.getFileName(siteFile),
                        destinationfilename : fileName,

                    })
                    const result = await lastValueFrom(this.authHttp.post(url, {}));
                    return result.json();
                }
                return false;
            }, 30);
        }
        return this.sendFileActions[actionName];
    }

    private getParameterFileUrlActionName(actionName){
        return (actionName === 'parameter') ? this.config.services.upgrade.sendParameterFile : this.config.services.upgrade.sendFile;
    }

    private getFileName(siteFile){
        return (siteFile) ? siteFile.SiteFileId || siteFile.id : null;
    }

    public async openConfirmDialog(title: string, text: string){
        return new Promise(async(resolve, reject) => {
            const dialog = custom({
                title,
                message: text,
                buttons: [
                    {
                        text: __i18n('Yes', 'form.data.has.changed.dialog.button.yes'),
                        onClick: () => {
                            dialog.hide();
                            resolve(true);
                        }
                    },
                    {
                        text: __i18n('No', 'form.data.has.changed.dialog.button.no'),
                        accessKey: 'esc',
                        onClick: () => {
                            dialog.hide();
                            resolve(false);
                        }
                    }
                ]
            });

            try {
                await dialog.show();
            }catch(e) {
                dialog.hide();
                resolve(false);
            }
        });
    }

    public pollTime = 5000;

    public pollService: SerializedPollService = new SerializedPollService(async() => {
        await this.getData();
    }, this.pollTime)


    public subject : BehaviorSubject<any> = new BehaviorSubject({});
    public observable = this.subject.asObservable();

    public async startStream(){
        this.pollService.start();
        return this.observable;
    }

    public stopStream(){
        this.pollService.stop();
    }

    public async getData(){
        const result = await this.getUpgradeInfos();
        this.subject.next(result);
    }

    public async getUpgradeInfos(){
        const url = this.urlEnhancerService.replaceParams(this.config.services.upgrade.fileInfo);
        return lastValueFrom(this.authHttp.getJSON(url));
    }

    public async executeCommand(command: UpgradeCommands, data = {}){
        const url = this.urlEnhancerService.replaceParams(this.config.services.upgrade.command, {
            commandname : command
        })
        return lastValueFrom(this.authHttp.postJSON(url, data));
    }

    public getActions() {
        return [
            new UpgradeProcess(__i18n('Restart controller', 'upgrade.action.restart.text'), async() => {
                    if(await this.openConfirmDialog(
                        __i18n('Confirm', 'upgrade.confirm.action.dialog.topic'),
                        __i18n('Really reset the plant controller?', 'upgrade.confirm.action.dialog.restart-controller.text')
                    )) {
                       const result = await this.executeCommand(UpgradeCommands.restartcontroller);
                       return result;
                    }
                    return false;
                },
                3,
                'operatingupgrade.button.resetcontroller',
                'operatingupgrade.label.resetcontrollerstatus'
            ),

            new UpgradeProcess(__i18n('Stop application', 'upgrade.action.stop.text'), async() => {
                    if(await this.openConfirmDialog(
                        __i18n('Confirm', 'upgrade.confirm.action.dialog.topic'),
                        __i18n('Really stop the application?', 'upgrade.confirm.action.dialog.stop-application.text')
                    )) {
                        const result = await this.executeCommand(UpgradeCommands.stopcontrollerapplication);
                        return result;
                    }
                    return false;
                },
                4,
                'operatingupgrade.button.stopapplication',
                'operatingupgrade.label.stopapplicationstatus'
            ),

            new UpgradeProcess(__i18n('Start upgrade process', 'upgrade.action.start-upgrade.text'), async() => {
                    if(await this.openConfirmDialog(
                        __i18n('Confirm', 'upgrade.confirm.action.dialog.topic'),
                        __i18n('Really start the upgrade for the plant controller?', 'upgrade.confirm.action.dialog.start-upgrade-process.text')
                    )) {
                        const result = await this.executeCommand(UpgradeCommands.startupgrade);
                        return result;
                    }
                    return false;
                },
                7,
                'operatingupgrade.button.startupgrade',
                'operatingupgrade.label.startupgradestatus'
            ),

            new UpgradeProcess(__i18n('Start application', 'upgrade.action.start-application.text'), async() => {
                    if(await this.openConfirmDialog(
                        __i18n('Confirm', 'upgrade.confirm.action.dialog.topic'),
                        __i18n('Really start the application?', 'upgrade.confirm.action.dialog.start-application.text')
                    )) {
                        const result = await this.executeCommand(UpgradeCommands.startcontrollerapplication);
                        return result;
                    }
                    return false;
                },
                5,
                'operatingupgrade.button.startapplication',
                'operatingupgrade.label.startapplicationstatus'
            ),

            new UpgradeProcess(__i18n('Upload cloud mapping', 'upgrade.action.upload-cloud-mapping.text'), async() => {
                    if(await this.openConfirmDialog(
                        __i18n('Confirm', 'upgrade.confirm.action.dialog.topic'),
                        __i18n('Really upload the description?', 'upgrade.confirm.action.dialog.upload-cloud-mapping.text')
                    )) {
                        const result = await this.executeCommand(UpgradeCommands.uploadcloudmapping);
                        return result;
                    }
                    return false;
                },
                9,
                'operatingupgrade.button.uploaddescription',
                'operatingupgrade.label.uploaddescriptionstatus'
            ),

            new UpgradeProcess(
                __i18n('Create controller trace', 'upgrade.action.create-controller-trace.text'),
                async() => {
                    if(await this.openConfirmDialog(
                        __i18n('Confirm', 'upgrade.confirm.action.dialog.topic'),
                        __i18n('Really create controller trace file?', 'upgrade.confirm.action.dialog.create-controller-trace.text')
                    )) {
                        const result = await this.executeCommand(UpgradeCommands.createanduploadcontrollertracefile);
                        return result;
                    }
                    return false;
                },
                10,
                'operatingupgrade.button.createtracefile',
                'operatingupgrade.label.createtracefilestatus'
            ),

            new UpgradeProcess(
                __i18n('Create parameter file', 'upgrade.action.create-parameter-file.text'),
                async() => {
                    if(await this.openConfirmDialog(
                        __i18n('Confirm', 'upgrade.confirm.action.dialog.topic'),
                        __i18n('Really create parameter file?', 'upgrade.confirm.action.dialog.create-parameter-file.text')
                    )) {
                        const result = await this.executeCommand(UpgradeCommands.createanduploadparameterfile);
                        return result;
                    }
                    return false;
                },
                11,
                'operatingupgrade.button.createparameterfile',
                'operatingupgrade.label.createparameterfilestatus'
            ),

            new UpgradeProcess(__i18n('Create BACnet file', 'upgrade.action.create-bacnet-file.text'), async() => {
                    if(await this.openConfirmDialog(
                        __i18n('Confirm', 'upgrade.confirm.action.dialog.topic'),
                        __i18n(
                            'Really upload the BACnet file?',
                            'upgrade.confirm.action.dialog.create-bacnet-file.text'
                        )
                    )) {
                        /*
                        const utcDiff = new Date().getTimezoneOffset();
                        const commandResult = await upgradeResource.proxy.uploadBacNetConfigFile(
                            tenantId, plantId, utcDiff
                        );*/
                        const result = await this.executeCommand(UpgradeCommands.uploadbacnetfile);
                        return result;
   
                    }
                    return false;
                },
                12,
                'operatingupgrade.button.createbacnetfile',
                'operatingupgrade.label.createbacnetfilestatus'
            )
        ];
    }

    public reset(){
        this.sendFileActions = {};
        this.stopStream();
    }

    public async startUpgrade(data: any) {
        const result = await this.executeCommand(UpgradeCommands.scheduleautomaticupgrade, data);
        return result;
    }


    public acknowlegeUpgradPriority() {
        const url = this.urlEnhancerService.replaceParams(this.config.services.upgrade.acknowledgeUpgradePrio);
        return lastValueFrom(this.authHttp.putJSON(url, {}));
    }

}
